import { isAgentRowRevealer } from '@blocksuite/affine-shared/services';
import { agentDatabaseTargetElement } from '@blocksuite/affine-shared/utils';
import type { AgentPlayback, AgentPointerTarget } from '@blocksuite/store';
import type { Observable } from 'rxjs';

import type { AgentPointerCarry } from './agent-pointer-display';

/** Below this many screen pixels a row stayed where it was: a value changed, nothing moved. */
const MOVED_PX = 2;
/** A row the pointer does not come for (it went elsewhere) stands at its new place after this. */
const UNCLAIMED_MS = 2000;

interface RevealInput {
  playback: Observable<AgentPlayback>;
  /** The rendered database block of a target. */
  block(id: string): Element | null;
  view: Window;
  reduced(): boolean;
  /** The views drew the change: the pointer goes on to where the row is now. */
  drawn(): void;
  /** This window follows the crewmate: it may bring the target into view. */
  following(): boolean;
}

interface Carried {
  key: string;
  element: HTMLElement;
  timer: number;
  /** Old place minus new place, in the element's own pixels. */
  dx: number;
  dy: number;
  /** Screen pixels per own pixel (canvas zoom). */
  scale: number;
  /** The part of the way still ahead, 1 until the pointer sets out. */
  rest: number;
  done: boolean;
}

interface Target {
  blockId: string;
  rowId: string;
  columnId?: string;
}

interface Place {
  element: HTMLElement;
  rect: DOMRect;
  /** The row's place within its database block, in screen pixels. */
  x: number;
  y: number;
}

function keyOf(blockId: string, rowId: string): string {
  return `${blockId}:${rowId}`;
}

/**
 * Shows a crewmate's change of a database row as a person would make it: a
 * card, event or bar whose date or group changed is carried by the pointer
 * from its old place to the new one. The playback arrives while the views
 * still show the old state, so the old place is read then; after they drew
 * the change, the row is set back onto its old place (FLIP) and follows the
 * pointer's progress home. A view that draws later than the next frame (the
 * calendar draws from its worker projection) is watched until it does. Only client-local presentation: nothing is written.
 */
export class AgentDatabaseReveal {
  private readonly _carried = new Map<string, Carried>();
  /** Where a row stood that the change removed: the pointer shows the deletion there. */
  private readonly _gone = new Map<string, { rect: DOMRect; timer: number }>();
  /** Rows whose view had not drawn the change yet: carried once it does. */
  private readonly _watched = new Map<string, { stop(): void }>();
  private readonly _cleanup: (() => void)[] = [];
  private _disposed = false;

  constructor(private readonly input: RevealInput) {
    const subscription = input.playback.subscribe(playback =>
      this._play(playback)
    );
    this._cleanup.push(() => subscription.unsubscribe());
  }

  /** The screen place of a row the change removed, while the pointer shows it. */
  private _goneRect(target: AgentPointerTarget): DOMRect | null {
    if (!target.rowId) return null;
    return this._gone.get(keyOf(target.id, target.rowId))?.rect ?? null;
  }

  /**
   * Where the pointer goes for `target`, shown by `shown`: a carried row at
   * its new place (not where it is drawn while carried), a row the change
   * removed where it stood.
   */
  placeOf(target: AgentPointerTarget, shown: Element | null): DOMRect | null {
    if (!shown) return this._goneRect(target);
    const rect = shown.getBoundingClientRect();
    const carried =
      target.rowId && this._carried.get(keyOf(target.id, target.rowId));
    if (!carried || carried.done || carried.element !== shown) return rect;
    return new DOMRect(
      rect.left - carried.dx * carried.rest * carried.scale,
      rect.top - carried.dy * carried.rest * carried.scale,
      rect.width,
      rect.height
    );
  }

  /** What the pointer carries on its way to `target`, if anything. */
  carry(target: AgentPointerTarget): AgentPointerCarry | null {
    if (!target.rowId) return null;
    const carried = this._carried.get(keyOf(target.id, target.rowId));
    if (!carried || carried.done) return null;
    // A way that takes the row on starts where the last one left it.
    const start = carried.rest;
    return {
      moved: progress => {
        const rest = start * (1 - Math.min(1, Math.max(0, progress)));
        carried.rest = rest;
        carried.element.style.translate = `${carried.dx * rest}px ${carried.dy * rest}px`;
      },
      arrived: () => this._settle(carried.key),
    };
  }

  private _play(playback: AgentPlayback): void {
    if (this._disposed) return;
    const before = new Map<string, Place>();
    const targets: Target[] = [];
    for (const { target } of playback.steps) {
      if (!target.rowId) continue;
      const row: Target = {
        blockId: target.id,
        rowId: target.rowId,
        columnId: target.columnId,
      };
      const key = keyOf(row.blockId, row.rowId);
      this._settle(key);
      this._unwatch(key);
      const place = this._place(row);
      if (place) before.set(key, place);
      targets.push(row);
    }
    if (!targets.length) return;
    // Most views draw the change before the next frame is painted.
    this.input.view.requestAnimationFrame(() => {
      if (this._disposed) return;
      // A following window brings a target it does not show into view first
      // (pages the calendar, scrolls the table); the pointer goes there after.
      if (this.input.following()) {
        const revealed = targets.filter(({ blockId, rowId, columnId }) =>
          this._reveal(blockId, rowId, columnId)
        );
        if (revealed.length) {
          this.input.view.requestAnimationFrame(() =>
            this.input.view.requestAnimationFrame(() => {
              if (!this._disposed) this.input.drawn();
            })
          );
        }
      }
      if (!this.input.reduced()) {
        for (const row of targets) {
          const key = keyOf(row.blockId, row.rowId);
          const from = before.get(key);
          if (!from) continue;
          const now = this._place(row);
          if (now && this._carryFrom(key, from, now)) continue;
          if (!now) this._remember(key, from.rect);
          // A view that draws from its projection (the calendar) draws the
          // change later: the row is carried once it shows at its new place.
          this._watch(key, row, from);
        }
      }
      this.input.drawn();
    });
  }

  /** Where the view shows a row: on screen and within its database block. */
  private _place({ blockId, rowId, columnId }: Target): Place | null {
    const block = this.input.block(blockId);
    const element =
      block && agentDatabaseTargetElement(block, { rowId, columnId });
    if (!block || !(element instanceof HTMLElement)) return null;
    const rect = element.getBoundingClientRect();
    const frame = block.getBoundingClientRect();
    return {
      element,
      rect,
      x: rect.left - frame.left,
      y: rect.top - frame.top,
    };
  }

  /** Sets a row the change moved back onto its old place; false when it did not move. */
  private _carryFrom(key: string, from: Place, now: Place): boolean {
    // Within the block, so a canvas pan or zoom in between is no move.
    const x = from.x - now.x;
    const y = from.y - now.y;
    if (Math.hypot(x, y) < MOVED_PX) return false;
    // Canvas zoom scales the row: its own pixels are screen pixels over that scale.
    const scale =
      now.element.offsetWidth > 0
        ? now.rect.width / now.element.offsetWidth
        : 1;
    const carried: Carried = {
      key,
      element: now.element,
      timer: this.input.view.setTimeout(() => this._settle(key), UNCLAIMED_MS),
      dx: x / scale,
      dy: y / scale,
      scale,
      rest: 1,
      done: false,
    };
    now.element.style.translate = `${carried.dx}px ${carried.dy}px`;
    this._carried.set(key, carried);
    this._forget(key);
    return true;
  }

  /** Carries the row once its view draws it elsewhere, within UNCLAIMED_MS. */
  private _watch(key: string, row: Target, from: Place): void {
    const block = this.input.block(row.blockId);
    if (!block) return;
    let frame = 0;
    const timer = this.input.view.setTimeout(
      () => this._unwatch(key),
      UNCLAIMED_MS
    );
    const observer = new MutationObserver(() => {
      if (frame) return;
      frame = this.input.view.requestAnimationFrame(() => {
        frame = 0;
        if (this._disposed) return;
        const now = this._place(row);
        if (!now || !this._carryFrom(key, from, now)) return;
        this._unwatch(key);
        this.input.drawn();
      });
    });
    observer.observe(block, {
      subtree: true,
      childList: true,
      attributes: true,
    });
    this._watched.set(key, {
      stop: () => {
        observer.disconnect();
        if (frame) this.input.view.cancelAnimationFrame(frame);
        this.input.view.clearTimeout(timer);
      },
    });
  }

  private _unwatch(key: string): void {
    this._watched.get(key)?.stop();
    this._watched.delete(key);
  }

  /** Asks the database block to bring a row into view when it is not on screen; true when it did. */
  private _reveal(blockId: string, rowId: string, columnId?: string): boolean {
    const block = this.input.block(blockId);
    if (!isAgentRowRevealer(block)) return false;
    const shown = agentDatabaseTargetElement(block, { rowId, columnId });
    if (shown && this._onScreen(shown.getBoundingClientRect())) return false;
    return block.revealAgentRow(rowId);
  }

  private _onScreen(rect: DOMRect): boolean {
    const { innerWidth, innerHeight } = this.input.view;
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < innerHeight &&
      rect.left < innerWidth
    );
  }

  private _remember(key: string, rect: DOMRect): void {
    const previous = this._gone.get(key);
    if (previous) this.input.view.clearTimeout(previous.timer);
    this._gone.set(key, {
      rect,
      timer: this.input.view.setTimeout(() => {
        this._gone.delete(key);
        this.input.drawn();
      }, UNCLAIMED_MS),
    });
  }

  private _forget(key: string): void {
    const gone = this._gone.get(key);
    if (!gone) return;
    this.input.view.clearTimeout(gone.timer);
    this._gone.delete(key);
  }

  private _settle(key: string): void {
    const carried = this._carried.get(key);
    if (!carried) return;
    carried.done = true;
    this.input.view.clearTimeout(carried.timer);
    carried.element.style.translate = '';
    this._carried.delete(key);
  }

  dispose(): void {
    this._disposed = true;
    for (const key of [...this._carried.keys()]) this._settle(key);
    for (const key of [...this._watched.keys()]) this._unwatch(key);
    for (const { timer } of this._gone.values())
      this.input.view.clearTimeout(timer);
    this._gone.clear();
    for (const cleanup of this._cleanup.splice(0)) cleanup();
  }
}
